import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { GetStaticPropsResult } from 'next';
import { FormattedMessage } from 'react-intl';
import Link from 'next/link';
import PromoBanner from '@/components/PromoBanner';
import {
  GallerySingleDrop,
  MergedSingleDrop,
  PromoDrop,
} from '@/types/singleDrop';
import { mergeSingleDrops, updatePromoDrops } from '@/lib/utils/helpers';
import DropList from '@/components/DropList';
import Footer from '@/components/Footer';
import { useInventoryStore } from '@/stores';
import { dropsApi } from '@/lib/api';
import Typography from '@/components/ui/Typography';
import Carousel from '@/components/ui/Carousel/Carousel';
import styles from './GalleryPage.module.css';
import Button from '@/components/ui/Button';
import { ROUTE } from '@/lib/constants';
import Label from '@/components/ui/Label';
import { XAContext } from '@/providers/XAProvider';

type GalleryPageProps = {
  singleDrops: GallerySingleDrop[];
  promoDrops: PromoDrop[];
};

function GalleryPage(
  {
    singleDrops,
    promoDrops,
  }: GalleryPageProps,
): React.ReactElement {
  const xa = useContext(XAContext);

  const inventoryDrops = useInventoryStore((state) => state.drops);

  const [gallerySingleDrops, setGallerySingleDrops] = useState<MergedSingleDrop[]>(singleDrops);

  const [galleryPromoDrops, setGalleryPromoDrops] = useState(promoDrops);

  const updatePromoInfo = () => {
    updatePromoDrops(galleryPromoDrops).then(setGalleryPromoDrops);
  };

  useEffect(() => {
    if (inventoryDrops.length > 0) {
      const mergeDrops = mergeSingleDrops(
        singleDrops,
        inventoryDrops,
      );

      setGallerySingleDrops(mergeDrops);
      updatePromoInfo();
    } else {
      setGallerySingleDrops(singleDrops);
      setGalleryPromoDrops(promoDrops);
    }
  }, [inventoryDrops.length]);

  const getCarouselContent = () => galleryPromoDrops.map((item, index) => {
    const dropExternalLink = `https://x.la/${item.slug}`;
    const dropInternalLink = `${ROUTE.DROP_ROUTE}/${item.id}`;

    return (
      <PromoBanner
        key={`promo-banner-${index + 1}`}
        media_url={item.promo_image_url}
        priority
      >
        <div className={classNames(
          styles['gallery-page__promo-content-wrapper'],
          'content-wrapper',
        )}
        >
          <div className={styles['gallery-page__promo-title-container']}>
            <div className="h-[28px]">
              {item.isClaimed && (
                <Label>
                  <FormattedMessage id="drop_card.collected_label" />
                </Label>
              )}
            </div>
            <Typography variant="h2" className={styles['gallery-page__promo-title']} color="primary">
              {item.title}
            </Typography>
            <Typography variant="p2" color="primary" className={styles['gallery-page__promo-description']}>
              {item.description}
            </Typography>
            <Typography variant="l2" color="primary">
              {item.promo_title}
            </Typography>
            {item.isClaimed
              ? (
                <Link href={dropInternalLink}>
                  <Button color="primary" size="large">
                    Open reward
                  </Button>
                </Link>
              )
              : (
                <a
                  href={dropExternalLink}
                  onClick={() => xa.hit(dropExternalLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button color="primary" size="large">
                    Claim now
                  </Button>
                </a>
              )}
          </div>
        </div>
      </PromoBanner>
    );
  });

  return (
    <div className={styles['gallery-page']}>
      {!!promoDrops.length && (
        <Carousel optionalClasses={styles['gallery-page__promo-container']}>
          {getCarouselContent()}
        </Carousel>
      )}
      <div className="background-container">
        <div
          className={classNames(styles['gallery-page__content-wrapper'])}
        >
          {!!singleDrops.length && (
            <div className={classNames(
              'content-wrapper',
              'content-wrapper--with-spacing',
              styles['gallery-page__drop-list-container'],
            )}
            >
              <DropList singleDrops={gallerySingleDrops} isLoading={false} />
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default GalleryPage;

export async function getStaticProps(): Promise<GetStaticPropsResult<GalleryPageProps>> {
  const singleDrops = await dropsApi.getSingleDrops();
  const promoDrops = await dropsApi.getDropPromotions();

  return {
    props: { singleDrops, promoDrops },
    revalidate: 60,
  };
}
